/**
 * Phase State Machine — aiming, firing, di_window, awaiting_next_turn, death_sequence, game_over
 *
 * Returns GameEffect[] instead of calling renderers directly.
 * All renderer dependencies have been removed — pure state mutation + effect descriptors.
 */

import { Keys, Time } from 'bonkjs';
import type { InputProvider } from '../input';
import type { GameState } from '../types';
import type { GameEffect } from './effects';
import { transitionPhase } from './effects';
import { updateAiming } from './aiming';
import { updateMovement } from './movement';
import { triggerVoidDeathSequence, triggerSkyDeathSequence, triggerSideDeathSequence } from './death';
import { resetGame } from './reset';
import { getGroundY, getGroundYBelow } from '../terrain/grid';
import { PHYSICS, GAME_OVER, TANK_VISUAL, MUZZLE, TURN_FLOW, VOID, KNOCKBACK, GRAPPLE_FX, DI, BURST, BRACE, LAUNCH_FX, ROUND_START, WORLD, ACTION, MOMENTUM, TARGET_BLAST, TARGET_BLAST_INTRO, LOTTERY, HOP, EDGEGUARD, ANNOUNCE, ASCENT, CEILING_BOUNCE, STOCKS, TUTORIAL, SURVIVAL, SIDE_DEATH } from '../constants';
import { isSoloMode, modeUsesLottery, modeUsesShotTiers, modeIsAscent, modeIsClassic, modeIsRoguelite, modeUsesStocks, modeIsSurvival } from './mode';
import { ROGUELITE } from '../constants';
import { checkForCheckpoint } from '../roguelite/checkpoint';
import { applyCardChoice } from '../roguelite/card-draw';
import { getTankShot, getAllTankIds, lookupTank } from '../tanks';
import { getSkinForTank } from '../skins/storage';
import { rollLottery } from './lottery';
import { saveBestTime } from './best-times';
import { updateGrapple, getRopeTerrainContacts } from './grapple';
import { updateDIWindow } from './di';
import { startBurstWindow, updateBurstWindow } from './burst';

import { msToFrames, justPressed } from './input-edge';
import { handleMenuInput, handlePauseInput, handleWeaponLabInput, handleDisconnectOverlayInput, handleTutorialPauseInput } from './phase-menu';
import { DEFAULT_STAGE_ID } from '../stages';
import { processVoidRetaliation } from './void-retaliation';
import { handleFiringPhase } from './phase-firing';
import { handleAwaitingPhase } from './phase-awaiting';
import { handleDefensiveMovement, returnToDefenseFromBurst } from './defense';
import { startDefensivePhase } from './defense';
import { updateHopLift, updateFastFall } from './hop';
import { updateEdgeguardPhase } from './phase-edgeguard';
import { addCoins } from '../progression/storage';
import { computeCoinAward } from '../progression/coins';
import { getDefaultWeapon } from '../weapons';
import { mirrorSettle, mirrorDead } from '../physics/transitions';
import { fireCollapse } from './vortex-holes';
import { getGlitchRecoilForce } from './glitch';
import { applyBurstTumbleDI } from './burst-di';
import { openTrickSession } from './trick-score';
import { executeRespawn } from './respawn';


export function updatePhase(
  state: GameState,
  input: InputProvider,
  recoveryInput?: InputProvider,
): GameEffect[] {
  const fx: GameEffect[] = [];

  // Menu phases — no game logic, just input handling
  if (state.phase === 'title_screen' || state.phase === 'mode_select' || state.phase === 'skins_shop' || state.phase === 'tank_select' || state.phase === 'stage_select' || state.phase === 'mp_lobby' || state.phase === 'mp_waiting') {
    handleMenuInput(state, input, fx);
    return fx;
  }

  // Tick respawn timers for dead players awaiting respawn (runs every gameplay phase)
  if (modeUsesStocks(state.modeState.mode)) {
    for (let i = 0; i < state.players.length; i++) {
      const p = state.players[i];
      if (!p.alive && p.respawnTimer > 0) {
        p.respawnTimer--;
        if (p.respawnTimer <= 0) {
          const respawnFx = executeRespawn(state, i);
          fx.push(...respawnFx);
        }
      }
    }
  }

  // Disconnect overlay — freezes game logic, handles its own input
  // (game_over handles disconnect inline via post-match buttons)
  if (state.multiplayer?.disconnectPhase && state.phase !== 'game_over') {
    handleDisconnectOverlayInput(state, input, fx);
    return fx;
  }

  // Pause handling — ESC during aiming toggles pause
  if (state.phase === 'aiming' && justPressed(Keys.Escape, input) && !state.paused) {
    state.paused = true;
    state.pauseSelectIndex = 0;
    return fx;
  }
  if (state.paused) {
    if (state.modeState.mode === 'training') {
      handleWeaponLabInput(state, input, fx);
    } else if (state.modeState.mode === 'tutorial') {
      handleTutorialPauseInput(state, input, fx);
    } else {
      handlePauseInput(state, input, fx);
    }
    return fx;
  }

  // Tutorial complete — auto-return to menu after hold timer (phase-independent)
  if (state.modeState.mode === 'tutorial' && state.modeState.tutorialComplete) {
    const elapsed = (state.frameCount - state.modeState.tutorialStepFrame) / 60;
    if (elapsed >= TUTORIAL.COMPLETE_HOLD_SECONDS) {
      transitionPhase(state, 'title_screen', fx);
      fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
    }
    return fx;
  }

  // Multiplayer forfeit (F key during gameplay)
  if (state.multiplayer?.enabled && justPressed(Keys.F, input) && state.phase === 'aiming') {
    state.multiplayer.lobbyStatus = 'FORFEIT';
    // Set opponent as winner
    const opponentSlot = state.multiplayer.mySlot === 0 ? 1 : 0;
    state.winner = opponentSlot;
    transitionPhase(state, 'game_over', fx);
    state.gameOverTimer = 0;
    state.postMatchReady = false;
    state.postMatchCursor = 0;
    state.coinsAwarded = false;
    return fx;
  }

  // Debug: Ctrl+D to force desync (multiplayer dev tool)
  if (import.meta.env.DEV && state.multiplayer?.enabled && input.getKey(Keys.ControlLeft) && justPressed(Keys.D, input) && state.phase === 'aiming') {
    state.players[0].x += 50; // nudge P1 position to force hash mismatch
    console.warn('[DEBUG] Forced desync — player 0 nudged +50px');
  }

  // --- Boundary collision checks (runs before phase switch) ---
  // Catches void/sky kills during aiming, firing, and awaiting_next_turn
  if (state.phase !== 'death_sequence' && state.phase !== 'game_over') {
    const isSolo = isSoloMode(state.modeState.mode);
    for (let i = 0; i < state.players.length; i++) {
      const p = state.players[i];
      if (!p.alive || p.pendingDeath) continue;

      // Solo modes (except survival): P1 (index 0) can't die — teleport back to spawn
      if (isSolo && i === 0 && state.modeState.mode !== 'survival') {
        const soloSkyLimit = CEILING_BOUNCE.ENABLED ? -Infinity : KNOCKBACK.UPPER_BLAST_ZONE;
        const soloSideLeft = SIDE_DEATH.OPEN_SIDES ? WORLD.WORLD_LEFT - SIDE_DEATH.BLAST_ZONE_OFFSET : -Infinity;
        const soloSideRight = SIDE_DEATH.OPEN_SIDES ? WORLD.WORLD_RIGHT + SIDE_DEATH.BLAST_ZONE_OFFSET : Infinity;
        if (p.y + PHYSICS.ECB_RADIUS >= state.voidY || p.y - PHYSICS.ECB_RADIUS < soloSkyLimit
          || p.x - PHYSICS.ECB_RADIUS < soloSideLeft || p.x + PHYSICS.ECB_RADIUS > soloSideRight) {
          // Respawn P1 at stage center
          const centerX = (state.stageLeft + state.stageRight) / 2;
          p.x = centerX;
          p.y = getGroundY(centerX) - PHYSICS.ECB_RADIUS;
          p.vx = 0;
          p.vy = 0;
          p.tumble = false;
          p.hitstunFrames = 0;
          p.settled = true;
          p.settledX = p.x;
          p.settledY = p.y;
          mirrorSettle(p);
          p.damagePercent = 0;
        }
        continue;
      }

      // Void kill (below)
      if (p.y + PHYSICS.ECB_RADIUS >= state.voidY) {
        if (import.meta.env.DEV) console.error(`[KILL] VOID P${i} y=${p.y} ecb=${PHYSICS.ECB_RADIUS} bottom=${p.y + PHYSICS.ECB_RADIUS} voidY=${state.voidY} phase=${state.phase} frame=${state.frameCount}`);
        const deathFx = triggerVoidDeathSequence(state, i);
        if (deathFx.length > 0) {
          fx.push(...deathFx);
          fx.push({ type: 'death_triggered', playerIndex: i, deathType: 'void', x: p.x, y: p.y });
        }
        break; // one kill per frame
      }

      // Sky kill (above) — ascent mode uses a dynamic ceiling that tracks the void (1.5× viewport band)
      const skyThreshold = modeIsAscent(state.modeState.mode)
        ? Math.max(ASCENT.CEILING_BLAST_ZONE, state.voidY - ASCENT.BAND_HEIGHT)
        : (CEILING_BOUNCE.ENABLED ? CEILING_BOUNCE.STANDARD_CEILING_Y : KNOCKBACK.UPPER_BLAST_ZONE);
      if (p.y - PHYSICS.ECB_RADIUS < skyThreshold) {
        if (!CEILING_BOUNCE.ENABLED) {
          // Legacy: sky death
          if (import.meta.env.DEV) console.error(`[KILL] SKY P${i} y=${p.y} ecb=${PHYSICS.ECB_RADIUS} top=${p.y - PHYSICS.ECB_RADIUS} threshold=${skyThreshold} phase=${state.phase} frame=${state.frameCount}`);
          const deathFx = triggerSkyDeathSequence(state, i);
          if (deathFx.length > 0) {
            fx.push(...deathFx);
            fx.push({ type: 'death_triggered', playerIndex: i, deathType: 'sky', x: p.x, y: p.y });
          }
          break; // one kill per frame
        }
        // Bounce handled in physics — safety clamp only
        p.y = skyThreshold + PHYSICS.ECB_RADIUS;
      }

      // Side kill (left/right blast zones) — only when open sides enabled
      if (SIDE_DEATH.OPEN_SIDES) {
      const sideLeft = WORLD.WORLD_LEFT - SIDE_DEATH.BLAST_ZONE_OFFSET;
      const sideRight = WORLD.WORLD_RIGHT + SIDE_DEATH.BLAST_ZONE_OFFSET;
      if (p.x - PHYSICS.ECB_RADIUS < sideLeft || p.x + PHYSICS.ECB_RADIUS > sideRight) {
        const side = p.x < (WORLD.WORLD_LEFT + WORLD.WORLD_RIGHT) / 2 ? 'left' as const : 'right' as const;
        if (import.meta.env.DEV) console.error(`[KILL] SIDE P${i} x=${p.x} side=${side} phase=${state.phase} frame=${state.frameCount}`);
        const deathFx = triggerSideDeathSequence(state, i, side);
        if (deathFx.length > 0) {
          fx.push(...deathFx);
          fx.push({ type: 'death_triggered', playerIndex: i, deathType: 'side', x: p.x, y: p.y });
        }
        break;
      }
      }
    }
  }

  // --- Void rise effects trigger (first frame of rise notification) ---
  if (state.voidRisePending && state.voidRiseTimer > VOID.NOTIFICATION_TIME - VOID.RISE_TRIGGER_OFFSET) {
    fx.push({ type: 'void_rise_effects', voidY: state.voidY });
  }

  // --- Void retaliation processing (runs before phase switch) ---
  if (state.voidRetaliationActive && state.phase !== 'death_sequence' && state.phase !== 'game_over') {
    fx.push(...processVoidRetaliation(state));
  }

  switch (state.phase) {
    case 'round_start': {
      // Target Blast: custom intro sequence
      if (state.modeState.mode === 'target_blast') {
        const ms = state.modeState;
        const steps = TARGET_BLAST_INTRO.STEPS;

        // Camera holds stage center during intro
        const centerX = (state.stageLeft + state.stageRight) / 2;
        const centerY = WORLD.VIRTUAL_HEIGHT * 0.5;
        fx.push({ type: 'camera_follow', x: centerX, y: centerY });

        if (ms.introStep < steps.length) {
          ms.introTimer -= Time.fixedDeltaTime;
          // Push HUD effect for intro text
          fx.push({ type: 'hud_target_blast_intro', step: ms.introStep, timer: ms.introTimer });

          if (ms.introTimer <= 0) {
            ms.introStep++;
            if (ms.introStep < steps.length) {
              ms.introTimer = steps[ms.introStep].duration;
            }
          }
        }

        // All steps done — transition to aiming, timer starts now
        if (ms.introStep >= steps.length) {
          if (!input.getKey(ACTION.FIRE)) {
            state.roundStartFireReleased = true;
          }
          if (state.roundStartFireReleased) {
            state.burstFromSpecial = false;
            setDefaultShotTierWeapon(state);
            transitionPhase(state, 'aiming', fx);
            state.turnCountdown = 0; // no timer
            state.freezeUntil = state.frameCount + msToFrames(200);
          }
        }
        break;
      }

      // Training / Tutorial: skip round start cinematic — go straight to aiming
      if (state.modeState.mode === 'training' || state.modeState.mode === 'tutorial') {
        if (!input.getKey(ACTION.FIRE)) {
          state.roundStartFireReleased = true;
        }
        if (state.roundStartFireReleased) {
          state.burstFromSpecial = false;
          setDefaultShotTierWeapon(state);
          transitionPhase(state, 'aiming', fx);
          state.turnCountdown = 0; // no timer
          state.freezeUntil = state.frameCount + msToFrames(200);
        }
        break;
      }

      // Decrement timer
      state.roundStartTimer -= Time.fixedDeltaTime;

      // Track fire release (input gate — prevents held-fire bleed into aiming)
      if (!input.getKey(ACTION.FIRE)) {
        state.roundStartFireReleased = true;
      }

      // Camera: stage center initially, then pan to player after threshold
      // Ascent mode: skip camera_follow effects — main camera.follow callback handles it
      // (world center is Y=4000 which is meaningless in an 8000px world)
      if (!modeIsAscent(state.modeState.mode)) {
        const elapsed = ROUND_START.DURATION - state.roundStartTimer;
        if (elapsed < ROUND_START.PAN_TO_PLAYER_AT) {
          // Wide overview — center of stage
          const centerX = (state.stageLeft + state.stageRight) / 2;
          const centerY = WORLD.VIRTUAL_HEIGHT * 0.5;
          fx.push({ type: 'camera_follow', x: centerX, y: centerY });
        } else {
          // Pan to current player (camera lerps via followSmoothing)
          const p1 = state.players[state.currentPlayer];
          fx.push({ type: 'camera_follow', x: p1.x, y: p1.y });
        }
      }

      // Push HUD effect
      const progress = 1 - (state.roundStartTimer / ROUND_START.DURATION);
      fx.push({ type: 'hud_round_start', progress, roundNumber: state.roundNumber, turnNumber: state.turnNumber, visible: true });

      // Transition when timer expired AND fire released
      if (state.roundStartTimer <= 0 && state.roundStartFireReleased) {
        state.burstFromSpecial = false;
        // Cosmic Lottery mode: enter lottery before aiming
        if (modeUsesLottery(state.modeState.mode)) {
          enterLottery(state, fx);
        } else {
          setDefaultShotTierWeapon(state);
          transitionPhase(state, 'aiming', fx);
          state.turnCountdown = TURN_FLOW.TURN_DURATION;
        }
        state.freezeUntil = state.frameCount + msToFrames(TURN_FLOW.PHASE_CHANGE_FREEZE_MS);
      }
      break;
    }

    case 'burst_window': {
      // Skip while frozen
      if (state.frameCount < state.freezeUntil) break;

      // Process burst input and timer
      const burstComplete = updateBurstWindow(state, input);

      // Camera follows burst-active player during window
      if (state.burstActivePlayer >= 0) {
        const burstPlayer = state.players[state.burstActivePlayer];
        fx.push({ type: 'camera_follow', x: burstPlayer.x, y: burstPlayer.y });
      }

      if (burstComplete) {
        fx.push({ type: 'burst_hide' });

        const bi = state.currentPlayer;
        const bp = state.players[bi];

        if (bp.burstBraced) {
          // === Brace path — heal, green effects, no impulse ===
          bp.burstBraced = false;

          fx.push({
            type: 'burst_resolved',
            playerIndex: bi,
            vx: bp.vx, vy: bp.vy,
            x: bp.x, y: bp.y,
            momentum: bp.momentum,
            weaponCharge: bp.weaponCharge,
            hitstunFrames: bp.hitstunFrames,
            braced: true,
            damagePercent: bp.damagePercent,
          });

          fx.push({ type: 'screen_flash', color: BRACE.FLASH_COLOR, alpha: BRACE.FLASH_ALPHA });
          fx.push({ type: 'screen_shake', intensity: BRACE.SHAKE, decay: BRACE.SHAKE_DECAY });
          fx.push({ type: 'particles', method: 'spawnBraceHeal', args: [bp.x, bp.y, bp.color] });
          fx.push({
            type: 'tank_flash', playerIndex: bi,
            bright: BRACE.GREEN_BRIGHT, dark: BRACE.GREEN_DARK,
            duration: 0.25,
          });
          fx.push({ type: 'hud_heal_flash', playerIndex: bi });
        } else {
          // === Normal burst path — impulse + gold effects ===
          fx.push({
            type: 'burst_resolved',
            playerIndex: bi,
            vx: bp.vx, vy: bp.vy,
            x: bp.x, y: bp.y,
            momentum: bp.momentum,
            weaponCharge: bp.weaponCharge,
            hitstunFrames: bp.hitstunFrames,
            braced: false,
            damagePercent: bp.damagePercent,
          });

          const hasDirection = bp.burstInputX !== 0 || bp.burstInputY !== 0 || Math.abs(bp.vx) > 0.5 || Math.abs(bp.vy) > 0.5;
          const shakeIntensity = hasDirection ? BURST.RESOLVE_SHAKE_FULL : BURST.RESOLVE_SHAKE_NONE;
          fx.push({ type: 'screen_flash', color: BURST.GOLD, alpha: BURST.EXIT_FLASH_ALPHA });
          fx.push({ type: 'screen_shake', intensity: shakeIntensity, decay: BURST.RESOLVE_SHAKE_DECAY });
          fx.push({ type: 'particles', method: 'spawnBurstResolve', args: [bp.x, bp.y, bp.vx, bp.vy, bp.color] });

          if (hasDirection) {
            fx.push({
              type: 'tank_flash', playerIndex: bi,
              bright: BURST.GOLD_BRIGHT, dark: BURST.GOLD_DARK,
              duration: 0.25,
            });
          }
        }

        // Transition back to origin phase
        if (state.burstFromDefense) {
          // Return to defensive movement
          returnToDefenseFromBurst(state, fx);
        } else {
          // Return to aiming — preserve turn timer if triggered from special
          transitionPhase(state, 'aiming', fx);
          if (!state.burstFromSpecial) {
            state.turnCountdown = TURN_FLOW.TURN_DURATION;
          }
          state.burstFromSpecial = false;
        }
        state.freezeUntil = state.frameCount + msToFrames(TURN_FLOW.PHASE_CHANGE_FREEZE_MS);
      }
      break;
    }

    case 'lottery_spin': {
      // Camera follows current player during lottery
      const lotteryPlayer = state.players[state.currentPlayer];
      fx.push({ type: 'camera_follow', x: lotteryPlayer.x, y: lotteryPlayer.y });

      // Advance timer
      state.lotteryTimer += Time.fixedDeltaTime;

      // Stagger slot locks
      while (state.lotteryLockIndex < 4 && state.lotteryTimer >= LOTTERY.LOCK_TIMES[state.lotteryLockIndex]) {
        fx.push({ type: 'lottery_lock', slotIndex: state.lotteryLockIndex, weapon: state.lotterySlots[state.lotteryLockIndex] });
        state.lotteryLockIndex++;
      }

      // All 4 locked → transition to pick
      if (state.lotteryLockIndex >= 4) {
        transitionPhase(state, 'lottery_pick', fx);
        state.lotteryPickTimeout = LOTTERY.PICK_TIMEOUT;
      }
      break;
    }

    case 'lottery_pick': {
      // Camera follows current player during pick
      const pickPlayer = state.players[state.currentPlayer];
      fx.push({ type: 'camera_follow', x: pickPlayer.x, y: pickPlayer.y });

      // Read 1/2/3/4 input for slot selection
      let picked = -1;
      if (justPressed(Keys.Digit1, input)) picked = 0;
      else if (justPressed(Keys.Digit2, input)) picked = 1;
      else if (justPressed(Keys.Digit3, input)) picked = 2;
      else if (justPressed(Keys.Digit4, input)) picked = 3;
      // Arrow keys: left = slot 0, up = slot 1, right = slot 2, down = mortar (slot 3)
      else if (justPressed(Keys.ArrowLeft, input)) picked = 0;
      else if (justPressed(Keys.ArrowUp, input)) picked = 1;
      else if (justPressed(Keys.ArrowRight, input)) picked = 2;
      else if (justPressed(Keys.ArrowDown, input)) picked = 3;
      // Space = center slot (quick pick)
      else if (justPressed(Keys.Space, input)) picked = 1;

      // Timeout → auto-pick center
      state.lotteryPickTimeout -= Time.fixedDeltaTime;
      if (state.lotteryPickTimeout <= 0 && picked < 0) {
        picked = 1; // center slot
      }

      if (picked >= 0 && picked < 4) {
        pickPlayer.selectedWeapon = state.lotterySlots[picked];
        fx.push({ type: 'lottery_pick', slotIndex: picked });
        // lottery_hide deferred — LotteryRenderer auto-hides after pick fade animation

        // Transition to aiming
        transitionPhase(state, 'aiming', fx);
        state.turnCountdown = TURN_FLOW.TURN_DURATION;
        state.freezeUntil = state.frameCount + msToFrames(TURN_FLOW.PHASE_CHANGE_FREEZE_MS);
      }
      break;
    }

    case 'aiming': {
      // Update camera to follow current player
      const player = state.players[state.currentPlayer];
      fx.push({ type: 'camera_follow', x: player.x, y: player.y });

      // Solo modes (except survival): infinite momentum — refill each frame
      if (isSoloMode(state.modeState.mode) && state.modeState.mode !== 'survival') {
        player.momentum = MOMENTUM.MAX;
        player.momentumTurnStart = MOMENTUM.MAX;
        player.momentumActivated = false;
        player.idleFrames = 0;
      }

      // --- Weapon Lab: wind override, HMR re-derive, quick keys ---
      if (state.modeState.mode === 'training') {
        // Wind override: force wind to lab value each frame
        if (state.modeState.labWindOverride !== null) {
          state.wind = state.modeState.labWindOverride;
        }

        // HMR re-derive: pick up fresh weapon values after Vite hot reload
        player.selectedWeapon = getTankShot(player.tankId, player.selectedShotTier, player.terraformerMode).weapon;

        // R = reset dummy to spawn + apply lab damage + clear readout
        if (justPressed(Keys.R, input)) {
          const dummy = state.players[1];
          if (dummy) {
            const centerX = (state.stageLeft + state.stageRight) / 2;
            dummy.x = centerX + 200;
            dummy.y = getGroundY(dummy.x) - PHYSICS.ECB_RADIUS;
            dummy.vx = 0;
            dummy.vy = 0;
            dummy.tumble = false;
            dummy.hitstunFrames = 0;
            dummy.settled = true;
            dummy.settledX = dummy.x;
            dummy.settledY = dummy.y;
            mirrorSettle(dummy);
            dummy.damagePercent = state.modeState.labDummyDamage;
            dummy.alive = true;
            dummy.pendingDeath = false;
            state.modeState.labLastHit = null;
          }
        }

        // T = cycle tank forward, Q = cycle tank backward
        const tankIds = getAllTankIds();
        if (justPressed(Keys.T, input) || justPressed(Keys.Q, input)) {
          const dir = justPressed(Keys.T, input) ? 1 : -1;
          const curIdx = tankIds.indexOf(player.tankId);
          const nextIdx = (curIdx + dir + tankIds.length) % tankIds.length;
          player.tankId = tankIds[nextIdx];
          player.color = getSkinForTank(player.tankId).color ?? lookupTank(player.tankId).visual.bodyColor;
          player.selectedShotTier = 1;
          player.selectedWeapon = getTankShot(player.tankId, 1, player.terraformerMode).weapon;
          fx.push({ type: 'announce_world', text: lookupTank(player.tankId).name.toUpperCase(), x: player.x, y: player.y - 60, color: player.color, fontSize: 28, hold: 0.6 });
        }
      }

      // Target Blast: increment timer (runs during aiming, ticks across all phases below)
      if (state.modeState.mode === 'target_blast' && !state.modeState.runComplete) {
        state.modeState.elapsedTime += Time.fixedDeltaTime;
      }

      // Target Blast: exit gate collision — player walks into active gate
      if (state.modeState.mode === 'target_blast' && state.modeState.exitGateActive && !state.modeState.runComplete) {
        const dx = player.x - state.modeState.exitGateX;
        const dy = player.y - state.modeState.exitGateY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < TARGET_BLAST.GATE_RADIUS + PHYSICS.ECB_RADIUS) {
          // Run complete!
          state.modeState.runComplete = true;
          state.modeState.bestTimes = saveBestTime(state.modeState.elapsedTime);
          fx.push({ type: 'exit_gate_entered', x: state.modeState.exitGateX, y: state.modeState.exitGateY });
          fx.push({ type: 'screen_flash', color: 0x44ff88, alpha: 0.3 });
          fx.push({ type: 'screen_shake', intensity: 15, decay: 0.85 });
          fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [state.modeState.exitGateX, state.modeState.exitGateY, 0x44ff88, 80, 1.0] });
          transitionPhase(state, 'game_over', fx);
          state.gameOverTimer = 0;
          state.postMatchReady = false;
          state.postMatchCursor = 0;
          state.coinsAwarded = false;
          break;
        }
      }

      // Track special held state (edge detection, like fireHeld)
      const specialDown = input.getKey(ACTION.SPECIAL);
      const specialJustPressed = specialDown && !player.specialHeld;
      player.specialHeld = specialDown;

      // Turn timer countdown — always visible in non-solo modes
      if (state.turnCountdown > 0 && !isSoloMode(state.modeState.mode)) {
        state.turnCountdown -= Time.fixedDeltaTime;
        fx.push({ type: 'hud_update_turn_timer', time: state.turnCountdown });

        if (state.turnCountdown <= 0) {
          // Time's up — skip turn with "TIME!" announcement
          state.turnCountdown = 0;
          fx.push({ type: 'hud_hide_turn_timer' });
          fx.push({ type: 'announce_screen', text: 'TIME!', color: 0xff3333, fontSize: 56, hold: 0.6 });
          fx.push({ type: 'screen_shake', intensity: 8, decay: 0.85 });
          player.charging = false;
          player.power = 0;
          player.chargeGraceTimer = 0;
          player.fireHeld = false;
          transitionPhase(state, 'awaiting_next_turn', fx);
          state.turnTimer = TURN_FLOW.TURN_DELAY;
          break;
        }
      }

      // --- Terraformer mode switch (press 3) — free action, does NOT consume turn ---
      if (player.tankId === 'terraformer' && !player.tumble
          && justPressed(Keys.Digit3, input)) {
        player.terraformerMode = player.terraformerMode === 'mason' ? 'sapper' : 'mason';
        player.charging = false;
        player.power = 0;
        player.chargeGraceTimer = 0;
        player.fireHeld = false;
        player.selectedShotTier = 1;
        player.selectedWeapon = getTankShot('terraformer', 1, player.terraformerMode).weapon;
        const modeLabel = player.terraformerMode === 'sapper' ? 'SAPPER' : 'MASON';
        fx.push({ type: 'announce_world', text: modeLabel + ' MODE', x: player.x, y: player.y - 60, color: 0xffaa00, fontSize: 32, hold: 0.8 });
        fx.push({ type: 'screen_flash', color: 0xffaa00, alpha: 0.1 });
        fx.push({ type: 'screen_shake', intensity: 4, decay: 0.85 });
        break;
      }

      // --- Special (voluntary burst) — disabled in classic mode ---
      if (specialJustPressed && !player.specialUsed && !player.tumble && !modeIsClassic(state.modeState.mode)) {
        // Cancel any active charge
        player.charging = false;
        player.power = 0;
        player.chargeGraceTimer = 0;

        // Mark used + flag for turn timer preservation
        player.specialUsed = true;
        state.burstFromSpecial = true;

        // Enter burst window
        startBurstWindow(state, state.currentPlayer);
        transitionPhase(state, 'burst_window', fx);
        fx.push({ type: 'special_triggered', playerIndex: state.currentPlayer });
        fx.push({ type: 'burst_show' });
        fx.push({ type: 'screen_flash', color: BURST.ENTRY_FLASH_COLOR, alpha: BURST.ENTRY_FLASH_ALPHA });
        fx.push({ type: 'screen_shake', intensity: BURST.ENTRY_SHAKE, decay: BURST.ENTRY_SHAKE_DECAY });
        fx.push({ type: 'particles', method: 'spawnBurstEntry', args: [player.x, player.y, player.color] });
        break;
      }

      // Burst tumble DI — subtle velocity influence during post-burst hitstun
      if (player.tumble && player.burstFlying) {
        applyBurstTumbleDI(player, input);
      }

      // Movement runs first (sets walkDeltaX etc.), then aiming handles turret + fire.
      // Grapple gate: when grapple is active, skip movement (aiming handles grapple air control).
      const grappleActive = state.grapple.active !== null;
      const { reversed } = grappleActive ? { reversed: false } : updateMovement(state, input);

      // Hop & Lift — runs after movement, before aiming. Grapple gate: no hop during grapple.
      // Near-ground spatial coyote: if airborne but feet within threshold of terrain, hop instead of lift
      const feetY = player.y + PHYSICS.ECB_RADIUS;
      const groundBelow = getGroundYBelow(player.x, feetY);
      const nearGround = !player.grounded && (groundBelow - feetY) < HOP.NEAR_GROUND_PX;
      const hopResult = grappleActive
        ? { hopped: false, liftToggled: false, liftBurst: false }
        : updateHopLift(player, input, false, ACTION.JUMP, nearGround);

      if (hopResult.hopped) {
        fx.push({ type: 'particles', method: 'spawnHopDust', args: [player.x, player.y + PHYSICS.ECB_RADIUS, player.color] });
        fx.push({ type: 'screen_shake', intensity: 3, decay: 0.75 });
      }
      if (hopResult.liftBurst) {
        fx.push({ type: 'particles', method: 'spawnLiftBurst', args: [player.x, player.y, player.vx, player.vy, player.color] });
        fx.push({ type: 'screen_shake', intensity: 2.5, decay: 0.78 });
      }

      // Fast Fall — runs after hop/lift
      const fastFallResult = grappleActive
        ? { activated: false }
        : updateFastFall(player, input, false);
      if (fastFallResult.activated) {
        fx.push({ type: 'particles', method: 'spawnFastFallActivation', args: [player.x, player.y, player.color] });
      }

      const { fired, aimReversed, grappleEvent, turnConsumed, disarmed } = updateAiming(state, input);

      // --- Arming consumed the turn (requiresArming shot selected) ---
      if (turnConsumed) {
        fx.push({ type: 'hud_hide_turn_timer' });
        fx.push({ type: 'announce_world', text: 'ARMING!', x: player.x, y: player.y - 60, color: 0xff4400, fontSize: 32, hold: 0.8 });
        fx.push({ type: 'screen_flash', color: 0xff4400, alpha: 0.15 });
        fx.push({ type: 'screen_shake', intensity: 6, decay: 0.85 });
        transitionPhase(state, 'awaiting_next_turn', fx);
        state.turnTimer = TURN_FLOW.TURN_DELAY;
        break;
      }

      // --- Disarmed (selected a different tier while armed) ---
      if (disarmed) {
        fx.push({ type: 'announce_world', text: 'DISARMED', x: player.x, y: player.y - 60, color: 0xffaa00, fontSize: 28, hold: 0.6 });
        fx.push({ type: 'screen_shake', intensity: 3, decay: 0.8 });
      }

      if (reversed) {
        const rp = state.players[state.currentPlayer];
        fx.push({ type: 'particles', method: 'spawnDirectionChange', args: [rp.x, rp.y + PHYSICS.ECB_RADIUS, rp.prevMoveDir, rp.color] });
        fx.push({ type: 'screen_shake', intensity: 1.5, decay: 0.7 });
      }

      // Aim reversal — Sakurai: state changes deserve proportional visual pop
      if (aimReversed) {
        const tipX = player.x + Math.cos(-player.angle) * (TANK_VISUAL.TURRET_LENGTH + TANK_VISUAL.TIP_RADIUS);
        const tipY = player.y + Math.sin(-player.angle) * (TANK_VISUAL.TURRET_LENGTH + TANK_VISUAL.TIP_RADIUS);
        const tangent = -player.angle + (player.aimPrevDir > 0 ? -Math.PI / 2 : Math.PI / 2);
        fx.push({ type: 'particles', method: 'spawnAimSweepParticles', args: [tipX, tipY, tangent, player.color, 3, 0.35] });
        fx.push({ type: 'screen_shake', intensity: 0.8, decay: 0.85 });
      }

      // Grapple launch effect
      if (grappleEvent === 'launch') {
        const fireAngle = -player.angle;
        const tipX = player.x + Math.cos(fireAngle) * TANK_VISUAL.TURRET_LENGTH;
        const tipY = player.y + Math.sin(fireAngle) * TANK_VISUAL.TURRET_LENGTH;
        fx.push({ type: 'particles', method: 'spawnGrappleLaunch', args: [tipX, tipY, fireAngle, player.color] });
        fx.push({ type: 'screen_shake', intensity: GRAPPLE_FX.LAUNCH_SHAKE, decay: GRAPPLE_FX.LAUNCH_SHAKE_DECAY });
      }

      // Grapple update (runs during aiming phase)
      if (state.grapple.active) {
        const grappleResult = updateGrapple(state);

        // Camera follow: hook during travel, player during retraction
        if (state.grapple.active) {
          if (state.grapple.active.phase === 'traveling' && state.grapple.active.hook) {
            const hook = state.grapple.active.hook;
            fx.push({ type: 'camera_follow', x: hook.x, y: hook.y });
          } else {
            fx.push({ type: 'camera_follow', x: player.x, y: player.y });
          }
        }

        // Hook travel trail particles
        if (state.grapple.active?.phase === 'traveling' && state.grapple.active.hook) {
          const hook = state.grapple.active.hook;
          for (let t = 0; t < GRAPPLE_FX.TRAIL_RATE; t++) {
            fx.push({ type: 'particles', method: 'spawnGrappleTrail', args: [hook.x, hook.y, hook.vx, hook.vy, player.color] });
          }
        }

        if (grappleResult.event === 'attached') {
          fx.push({ type: 'particles', method: 'spawnGrappleAttach', args: [grappleResult.anchorX, grappleResult.anchorY, player.color] });
          fx.push({ type: 'screen_shake', intensity: GRAPPLE_FX.ATTACH_SHAKE, decay: GRAPPLE_FX.ATTACH_SHAKE_DECAY });
          fx.push({ type: 'screen_flash', color: player.color, alpha: GRAPPLE_FX.ATTACH_FLASH_ALPHA });
          fx.push({ type: 'camera_shake', intensity: GRAPPLE_FX.ATTACH_SHAKE / 2, decay: 0.85 });
        }

        if (grappleResult.event === 'arrived') {
          fx.push({ type: 'particles', method: 'spawnGrappleArrival', args: [grappleResult.anchorX, grappleResult.anchorY, player.color] });
          fx.push({ type: 'screen_shake', intensity: GRAPPLE_FX.ARRIVAL_SHAKE, decay: GRAPPLE_FX.ARRIVAL_SHAKE_DECAY });
          fx.push({ type: 'screen_flash', color: player.color, alpha: GRAPPLE_FX.ARRIVAL_FLASH_ALPHA });
          fx.push({ type: 'camera_shake', intensity: GRAPPLE_FX.ARRIVAL_SHAKE / 2, decay: 0.85 });
        }

        // Rope-terrain scrape sparks
        if (state.grapple.active?.phase === 'retracting') {
          const contacts = getRopeTerrainContacts(state);
          for (const contact of contacts) {
            if (Math.random() < GRAPPLE_FX.SCRAPE_SPARK_RATE) {
              fx.push({ type: 'particles', method: 'spawnRopeScrape', args: [contact.x, contact.y, player.color] });
            }
          }
        }
      }

      // Roguelite checkpoint detection — triggers card draw overlay
      if (modeIsRoguelite(state.modeState.mode) && state.currentPlayer === 0) {
        if (checkForCheckpoint(state, fx)) break;
      }

      if (fired) {
        fx.push({ type: 'hud_hide_turn_timer' });

        // Muzzle flash at turret tip
        const fireAngle = -player.angle; // screen-space direction
        const tipX = player.x + Math.cos(fireAngle) * TANK_VISUAL.TURRET_LENGTH;
        const tipY = player.y + Math.sin(fireAngle) * TANK_VISUAL.TURRET_LENGTH;

        // Hitscan weapons: fire beam immediately, skip projectile flight
        const firedWeapon = player.selectedWeapon ?? getDefaultWeapon();
        if (firedWeapon.hitscan) {
          fireCollapse(state, player, fx);
          // Purple muzzle flash for anti-gravity beam
          fx.push({ type: 'particles', method: 'spawnMuzzleFlash', args: [tipX, tipY, fireAngle, 0x9944ff] });
          fx.push({ type: 'screen_flash', color: 0x9944ff, alpha: 0.2 });
          fx.push({ type: 'screen_shake', intensity: MUZZLE.SHAKE_INTENSITY * 1.5, decay: MUZZLE.SHAKE_DECAY });
        } else {
          const muzzleMethod = firedWeapon.muzzleEffect ?? 'spawnMuzzleFlash';
          const muzzleShake = firedWeapon.muzzleShakeIntensity ?? MUZZLE.SHAKE_INTENSITY;
          fx.push({ type: 'particles', method: muzzleMethod, args: [tipX, tipY, fireAngle, player.color] });
          fx.push({ type: 'screen_flash', color: 0xff8800, alpha: MUZZLE.SCREEN_FLASH_ALPHA });
          fx.push({ type: 'screen_shake', intensity: muzzleShake, decay: MUZZLE.SHAKE_DECAY });
        }

        // HUGE_RECOIL: push the firing player backward from their shot
        const recoilForce = getGlitchRecoilForce(state);
        if (recoilForce > 0) {
          player.vx -= Math.cos(fireAngle) * recoilForce;
          player.vy -= Math.sin(fireAngle) * recoilForce;
          player.settled = false;
          player.grounded = false;
          fx.push({ type: 'screen_shake', intensity: 3, decay: 0.85 });
        }

        transitionPhase(state, 'firing', fx);
        fx.push({ type: 'hud_hide_turn_timer' });
        openTrickSession(state, fx);
      }
      break;
    }

    case 'firing': {
      handleFiringPhase(state, input, fx);
      break;
    }

    case 'di_window': {
      // Skip while frozen (hitstop from the explosion may still be active)
      if (state.frameCount < state.freezeUntil) break;

      // Process DI input and timer
      const diComplete = updateDIWindow(state, input);

      // Camera follows DI-active player during window
      if (state.diActivePlayer >= 0) {
        const diP = state.players[state.diActivePlayer];
        fx.push({ type: 'camera_follow', x: diP.x, y: diP.y });
      }

      if (diComplete) {
        // All DI windows resolved
        fx.push({ type: 'di_hide' });
        fx.push({ type: 'screen_flash', color: 0xffffff, alpha: DI.EXIT_FLASH_ALPHA });

        // Emit per-player DI resolve data for multiplayer sync
        const knockedIndices: number[] = [];
        for (let i = 0; i < state.players.length; i++) {
          const p = state.players[i];
          if (p.alive && p.tumble) {
            fx.push({ type: 'di_resolved', playerIndex: i, vx: p.vx, vy: p.vy, x: p.x, y: p.y, hitstunFrames: p.hitstunFrames });
            knockedIndices.push(i);
          }
        }

        // Sakurai-audited resolve particles + launch flash for each DI-affected player
        for (let i = 0; i < state.players.length; i++) {
          const p = state.players[i];
          if (p.alive && p.tumble) {
            fx.push({ type: 'particles', method: 'spawnDIResolve', args: [p.x, p.y, p.vx, p.vy, p.color] });
            fx.push({ type: 'tank_flash', playerIndex: i,
              bright: LAUNCH_FX.FLASH_BRIGHT, dark: LAUNCH_FX.FLASH_DARK,
              duration: LAUNCH_FX.FLASH_DURATION });
          }
        }

        // Route through edgeguard (VS modes + tutorial, weapon has edgeguard, hitstun above threshold) or directly to defense
        if (knockedIndices.length > 0 && (!isSoloMode(state.modeState.mode) || state.modeState.mode === 'tutorial' || state.modeState.mode === 'survival')) {
          const firedWeapon = state.players[state.currentPlayer].selectedWeapon ?? getDefaultWeapon();
          const maxHitstun = Math.max(...knockedIndices.map(i => state.players[i].hitstunFrames));

          const maxDamage = Math.max(...knockedIndices.map(i => state.players[i].damagePercent));
          if (firedWeapon.edgeguard && maxHitstun >= firedWeapon.edgeguard.threshold && maxDamage >= 0) {
            // Enter edgeguard — attacker gets railgun follow-up
            state.edgeguardFired = false;
            state.edgeguardKnockedIndices = knockedIndices;
            state.edgeguardChargeFrame = 0;
            state.edgeguardChargeTarget = firedWeapon.edgeguard.chargeFrames;
            // Reset attacker aim/charge state
            const att = state.players[state.currentPlayer];
            att.charging = false;
            att.power = 0;
            att.fireHeld = false;
            att.chargeGraceTimer = 0;
            att.chargeGracePower = 0;
            att.aimHeldFrames = 0;
            transitionPhase(state, 'edgeguard', fx);
            fx.push({ type: 'edgeguard_show' });
            fx.push({ type: 'announce_screen', text: 'EDGEGUARD!', color: 0x00ffff, fontSize: 56, hold: 0.5 });
            fx.push({ type: 'particles', method: 'spawnEdgeguardEntry', args: [att.x, att.y, att.color] });
            fx.push({ type: 'screen_flash', color: EDGEGUARD.ENTRY_FLASH_COLOR, alpha: EDGEGUARD.ENTRY_FLASH_ALPHA });
            fx.push({ type: 'screen_shake', intensity: EDGEGUARD.ENTRY_SHAKE, decay: EDGEGUARD.ENTRY_SHAKE_DECAY });
          } else {
            // Below threshold or weapon has no edgeguard — straight to defense
            transitionPhase(state, 'defensive_movement', fx);
            startDefensivePhase(state, knockedIndices, fx);
          }
        } else if (knockedIndices.length > 0) {
          // Solo mode — skip edgeguard, go straight to defense
          transitionPhase(state, 'defensive_movement', fx);
          startDefensivePhase(state, knockedIndices, fx);
        } else {
          transitionPhase(state, 'awaiting_next_turn', fx);
          state.turnTimer = TURN_FLOW.TURN_DELAY;
        }
      }
      break;
    }

    case 'edgeguard': {
      updateEdgeguardPhase(state, input, fx);
      break;
    }

    case 'defensive_movement': {
      handleDefensiveMovement(state, input, fx);
      break;
    }

    case 'awaiting_next_turn': {
      handleAwaitingPhase(state, input, fx, recoveryInput);
      break;
    }

    case 'roguelite_card_draw': {
      // Camera follows human
      const cardPlayer = state.players[0];
      if (cardPlayer) {
        fx.push({ type: 'camera_follow', x: cardPlayer.x, y: cardPlayer.y });
      }

      // Void pause countdown
      const cdMs = state.modeState;
      if (cdMs.rogueliteVoidPauseTimer > 0) {
        cdMs.rogueliteVoidPauseTimer -= Time.fixedDeltaTime;
      }

      // Navigate cursor (0-2)
      if (justPressed(Keys.ArrowLeft, input) || justPressed(Keys.A, input)) {
        cdMs.rogueliteCardDrawCursor = Math.max(0, cdMs.rogueliteCardDrawCursor - 1);
        fx.push({ type: 'roguelite_card_draw_cursor', cursor: cdMs.rogueliteCardDrawCursor });
      }
      if (justPressed(Keys.ArrowRight, input) || justPressed(Keys.D, input)) {
        cdMs.rogueliteCardDrawCursor = Math.min(ROGUELITE.CARD_COUNT - 1, cdMs.rogueliteCardDrawCursor + 1);
        fx.push({ type: 'roguelite_card_draw_cursor', cursor: cdMs.rogueliteCardDrawCursor });
      }

      // Confirm selection
      if (justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) {
        applyCardChoice(state, cdMs.rogueliteCardDrawCursor);
        fx.push({ type: 'roguelite_card_draw_hide' });

        // Return to aiming
        transitionPhase(state, 'aiming', fx);
        state.freezeUntil = state.frameCount + msToFrames(TURN_FLOW.PHASE_CHANGE_FREEZE_MS);
      }
      break;
    }

    case 'death_sequence': {
      // Skip logic while frozen
      if (state.frameCount < state.freezeUntil) break;

      const hasStocks = modeUsesStocks(state.modeState.mode);

      // Kill players with pendingDeath (delayed from hitstop so tank stays visible during freeze)
      for (const p of state.players) {
        if (p.pendingDeath && p.alive) {
          p.alive = false;
          p.pendingDeath = false;
          mirrorDead(p);

          // Stock system: decrement stock, queue respawn if stocks remain
          if (hasStocks && p.stocks > 0) {
            p.stocks--;
            if (p.stocks > 0) {
              p.respawnTimer = STOCKS.RESPAWN_DELAY_FRAMES;
            }
          }
        }
      }

      // Belt-and-suspenders: clear edgeguard beam if it persisted into death_sequence
      if (state.edgeguardBeam) {
        state.edgeguardBeam = null;
      }

      state.turnTimer -= Time.fixedDeltaTime;

      if (state.turnTimer <= 0) {
        // Survival: game over when player dies, respawn enemy when enemy dies
        if (modeIsSurvival(state.modeState.mode)) {
          if (!state.players[0]?.alive) {
            // Player died — game over, save high score
            state.voidRetaliationQueue = [];
            state.voidRetaliationActive = false;
            state.winner = null;
            const ms = state.modeState;
            const currentScore = {
              kills: ms.survivalKills,
              score: state.trickScores[0] ?? 0,
              tank: state.players[0].tankId,
              date: new Date().toISOString().split('T')[0],
            };
            if (!ms.survivalHighScore
              || ms.survivalKills > ms.survivalHighScore.kills
              || (ms.survivalKills === ms.survivalHighScore.kills && currentScore.score > ms.survivalHighScore.score)) {
              ms.survivalHighScore = currentScore;
              try { localStorage.setItem(SURVIVAL.HIGH_SCORE_KEY, JSON.stringify(currentScore)); } catch { /* ignore */ }
            }
            transitionPhase(state, 'game_over', fx);
            state.gameOverTimer = 0;
            state.postMatchReady = false;
            state.postMatchCursor = 0;
            state.coinsAwarded = false;
          } else {
            // Enemy died — increment kills, heal player, retreat void
            const ms = state.modeState;
            ms.survivalKills++;
            state.players[0].damagePercent = Math.max(0, state.players[0].damagePercent - SURVIVAL.HEAL_ON_KILL);
            state.voidTargetY += SURVIVAL.VOID_REWARD;
            fx.push({ type: 'hud_heal_flash', playerIndex: 0 });
            transitionPhase(state, 'awaiting_next_turn', fx);
            state.turnTimer = SURVIVAL.SPAWN_DELAY_MS / 1000; // 1.5s delay before next enemy spawns
          }
          break;
        }

        // Solo modes: never game over — go back to awaiting (dummy respawns there)
        if (isSoloMode(state.modeState.mode)) {
          transitionPhase(state, 'awaiting_next_turn', fx);
          state.turnTimer = state.modeState.mode === 'tutorial' ? 0.1 : TURN_FLOW.TURN_DELAY;
          break;
        }

        // Roguelite: game over when human (P0) dies — enemies dying is normal progression
        if (modeIsRoguelite(state.modeState.mode)) {
          if (!state.players[0]?.alive) {
            state.voidRetaliationQueue = [];
            state.voidRetaliationActive = false;
            state.winner = null;
            transitionPhase(state, 'game_over', fx);
            state.gameOverTimer = 0;
            state.postMatchReady = false;
            state.postMatchCursor = 0;
            state.coinsAwarded = false;
          } else {
            // Enemies died — continue game
            transitionPhase(state, 'awaiting_next_turn', fx);
            state.turnTimer = TURN_FLOW.TURN_DELAY;
          }
          break;
        }

        if (hasStocks) {
          // Stock mode: game over when <=1 player has stocks remaining
          const playersWithStocks = state.players
            .map((p, i) => ({ stocks: p.stocks, alive: p.alive, index: i }))
            .filter(p => p.stocks > 0 || p.alive);

          if (playersWithStocks.length <= 1) {
            // Game over — clear retaliation queue
            state.voidRetaliationQueue = [];
            state.voidRetaliationActive = false;
            state.winner = playersWithStocks.length === 1 ? playersWithStocks[0].index : null;
            transitionPhase(state, 'game_over', fx);
            state.gameOverTimer = 0;
            state.postMatchReady = false;
            state.postMatchCursor = 0;
            state.coinsAwarded = false;
            if (state.winner !== null && state.multiplayer?.wins) {
              state.multiplayer.wins[state.winner]++;
            }
          } else {
            // Continue game — players still have stocks
            transitionPhase(state, 'awaiting_next_turn', fx);
            state.turnTimer = TURN_FLOW.TURN_DELAY;
          }
        } else {
          // Non-stock mode: check alive count
          const alivePlayers = state.players
            .map((p, i) => ({ alive: p.alive, index: i }))
            .filter(p => p.alive);

          if (alivePlayers.length <= 1) {
            // Game over — clear retaliation queue
            state.voidRetaliationQueue = [];
            state.voidRetaliationActive = false;
            state.winner = alivePlayers.length === 1 ? alivePlayers[0].index : null;
            transitionPhase(state, 'game_over', fx);
            state.gameOverTimer = 0;  // counts UP for post-match entrance animation
            state.postMatchReady = false;
            state.postMatchCursor = 0;
            state.coinsAwarded = false;
            // Track round wins in multiplayer
            if (state.winner !== null && state.multiplayer?.wins) {
              state.multiplayer.wins[state.winner]++;
            }
          } else {
            // Continue game — go through dialog flow
            transitionPhase(state, 'awaiting_next_turn', fx);
            state.turnTimer = TURN_FLOW.TURN_DELAY;
          }
        }
      }
      break;
    }

    case 'game_over': {
      // Advance entrance animation timer
      state.gameOverTimer += Time.fixedDeltaTime;

      // Award coins once on first frame
      if (!state.coinsAwarded) {
        // Clear lingering visuals — winner's portals survive death cleanup
        state.vortexHoles = [];
        state.coinsAwarded = true;
        const award = computeCoinAward(state);
        if (award > 0) addCoins(award);
      }

      // Post-match summary becomes interactive after entrance animation
      if (!state.postMatchReady && state.gameOverTimer >= 1.5) {
        state.postMatchReady = true;
      }

      // Block input until summary is ready
      if (!state.postMatchReady) break;

      // Navigate between Rematch (0) and Menu (1)
      if (justPressed(Keys.ArrowLeft, input) || justPressed(Keys.A, input)) {
        state.postMatchCursor = 0;
      }
      if (justPressed(Keys.ArrowRight, input) || justPressed(Keys.D, input)) {
        state.postMatchCursor = 1;
      }

      if (state.multiplayer?.enabled) {
        const opponentGone = !!state.multiplayer.disconnectPhase;

        // Block cursor from selecting REMATCH when opponent disconnected
        if (opponentGone && state.postMatchCursor === 0) {
          state.postMatchCursor = 1;
        }

        // Multiplayer — wait for rematch or leave
        if (justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) {
          if (state.postMatchCursor === 0 && !opponentGone) {
            state.multiplayer.lobbyStatus = 'REMATCH_REQUESTED';
          } else {
            state.multiplayer = null;
            transitionPhase(state, 'title_screen', fx);
            fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
          }
        }
        if (justPressed(Keys.Escape, input)) {
          state.multiplayer = null;
          transitionPhase(state, 'title_screen', fx);
          fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
        }
        break;
      }

      // Target Blast completion
      if (state.modeState.mode === 'target_blast' && state.modeState.runComplete) {
        if (justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) {
          if (state.postMatchCursor === 0) {
            for (const t of state.modeState.targets) t.hit = false;
            state.modeState.allTargetsHit = false;
            state.modeState.exitGateActive = false;
            state.modeState.runComplete = false;
            state.modeState.shotsFired = 0;
            state.modeState.elapsedTime = 0;
            state.modeState.introStep = 0;
            state.modeState.introTimer = TARGET_BLAST_INTRO.STEPS[0].duration;
            fx.push(...resetGame(state));
            state.freezeUntil = state.frameCount + msToFrames(TURN_FLOW.PHASE_CHANGE_FREEZE_MS);
          } else {
            transitionPhase(state, 'title_screen', fx);
            fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
          }
        }
        if (justPressed(Keys.Escape, input)) {
          transitionPhase(state, 'title_screen', fx);
          fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
        }
        break;
      }

      // All other modes — manual rematch/menu (no auto-countdown)
      if (justPressed(Keys.Space, input) || justPressed(Keys.Enter, input)) {
        if (state.postMatchCursor === 0) {
          // Rematch
          fx.push(...resetGame(state));
          state.freezeUntil = state.frameCount + msToFrames(TURN_FLOW.PHASE_CHANGE_FREEZE_MS);
        } else {
          // Return to menu
          transitionPhase(state, 'title_screen', fx);
          fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
        }
      }
      if (justPressed(Keys.Escape, input)) {
        transitionPhase(state, 'title_screen', fx);
        fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
      }
      break;
    }
  }

  return fx;
}

/** Set current player's weapon to their default (shot tier 1 for vs modes, mortar for standard modes). */
function setDefaultShotTierWeapon(state: GameState): void {
  const p = state.players[state.currentPlayer];
  if (modeUsesShotTiers(state.modeState.mode)) {
    p.selectedShotTier = 1;
    p.selectedWeapon = getTankShot(p.tankId, 1, p.terraformerMode).weapon;
  } else if (!modeUsesLottery(state.modeState.mode)) {
    p.selectedWeapon = getDefaultWeapon();
  }
}

/** Enter lottery phase — roll weapons and transition to lottery_spin. */
export function enterLottery(state: GameState, fx: GameEffect[]): void {
  const slots = rollLottery(state);
  state.lotterySlots = slots;
  state.lotteryTimer = 0;
  state.lotteryLockIndex = 0;
  state.lotteryPickTimeout = LOTTERY.PICK_TIMEOUT;
  const p = state.players[state.currentPlayer];
  transitionPhase(state, 'lottery_spin', fx);
  fx.push({ type: 'lottery_show', playerIndex: state.currentPlayer, x: p.x, y: p.y, slots });
}

// NOTE: Functions extracted to separate files in HEAD architecture:
// - justPressed() → input-edge.ts
// - handleMenuInput() → phase-menu.ts
// - handlePauseInput() → phase-menu.ts
// - processVoidRetaliation() → void-retaliation.ts
// - checkTargetHits() → phase-firing.ts
// - allTanksSettled() → phase-awaiting.ts
// Ben's lottery_spin/lottery_pick logic needs to be mapped into the extracted file structure.
